import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { PortalShell } from '@/components/nav/PortalShell';
import { portalApi } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { fmtMoney, fmtDate, fmtDocNumber } from '@/lib/format';
import { toast } from 'sonner';
import { CreditCard, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { PORTAL } from '@/constants/testIds';

export default function PortalInvoiceDetailPage() {
  const { id } = useParams();
  const [inv, setInv] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => portalApi.invoice(id).then(setInv);
  useEffect(() => { load(); }, [id]);

  const pay = async () => {
    try {
      const r = await portalApi.payInvoice(id, { method: 'card_on_file' });
      toast.success(r.invoice_status === 'paid' ? 'Payment received. Thank you!' : 'Partial payment recorded');
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Payment failed');
    }
  };

  if (!inv) return <PortalShell title="Invoice"><div className="text-sm text-muted-foreground">Loading…</div></PortalShell>;
  const paid = inv.status === 'paid';
  const remaining = (inv.total || 0) - (inv.total_paid || 0);

  return (
    <PortalShell title="Invoice">
      <Link to="/portal/invoices" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-4"><ArrowLeft className="h-3 w-3 mr-1" />Back to invoices</Link>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-start justify-between">
            <div>
              <CardTitle className="text-lg">{fmtDocNumber(inv, 'Invoice')}</CardTitle>
              <div className="text-xs text-muted-foreground mt-1">Issued {fmtDate(inv.created_at)}</div>
            </div>
            <Badge variant={paid ? 'default' : 'outline'}>{inv.status}</Badge>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 border-t pt-3">
              {(inv.line_items || []).map((li, i) => (
                <div key={i} className="flex justify-between text-sm">
                  <div>{li.label} <span className="text-muted-foreground">× {li.qty}</span></div>
                  <div className="tabular-nums">{fmtMoney((li.qty || 0) * (li.unit_price || 0))}</div>
                </div>
              ))}
              <div className="flex justify-between pt-3 border-t font-semibold">
                <div>Total</div><div className="tabular-nums">{fmtMoney(inv.total)}</div>
              </div>
              {inv.total_paid > 0 && (
                <>
                  <div className="flex justify-between text-sm text-muted-foreground"><div>Paid</div><div className="tabular-nums">{fmtMoney(inv.total_paid)}</div></div>
                  <div className="flex justify-between font-semibold"><div>Balance</div><div className="tabular-nums">{fmtMoney(remaining)}</div></div>
                </>
              )}
            </div>
            {inv.notes && <div className="mt-4 text-xs text-muted-foreground">{inv.notes}</div>}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Pay this invoice</CardTitle></CardHeader>
          <CardContent>
            {paid ? (
              <div className="flex items-center gap-1.5 text-sm text-[hsl(var(--fc-success))] font-semibold"><CheckCircle2 className="h-4 w-4" />This invoice is fully paid. Thank you!</div>
            ) : (
              <>
                <div className="text-sm mb-3">Balance due: <span className="font-semibold tabular-nums">{fmtMoney(remaining)}</span></div>
                <Button className="w-full" onClick={() => setOpen(true)} data-testid={PORTAL.invoicePay}><CreditCard className="h-4 w-4 mr-2" />Pay with card on file</Button>
                <div className="mt-2 text-[11px] text-muted-foreground text-center">Secure payment. Card processing via Stripe is coming soon — for now, this is a demo confirmation.</div>
              </>
            )}
            {inv.payments && inv.payments.length > 0 && (
              <div className="mt-4 border-t pt-3">
                <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Payment history</div>
                {inv.payments.map(p => (
                  <div key={p.id} className="flex justify-between text-xs py-1">
                    <span>{p.method} • {fmtDate(p.created_at)}</span>
                    <span className="tabular-nums">{fmtMoney(p.amount)}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Confirm payment</DialogTitle></DialogHeader>
          <div className="text-sm text-muted-foreground">
            You are about to pay <span className="font-semibold text-foreground">{fmtMoney(remaining)}</span> for invoice <b>{fmtDocNumber(inv, 'Invoice')}</b> using your saved card.
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={pay} data-testid={PORTAL.invoicePayConfirm}>Pay now</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PortalShell>
  );
}
